import Dexie, { type Table } from 'dexie';
import { dataURLToBlob } from './imageCompressor.js';
import { getAuthToken } from '../api/client.js';

export interface LocalCover {
  id: string; // bookId or a temp UUID
  blob: Blob;
  mimeType: string;
  fileName: string;
  syncStatus: 'pending' | 'synced' | 'failed';
  remoteUrl?: string;
  updatedAt: number;
}

let dexieDb: any = null;
let isIndexedDbSupported = false;

try {
  isIndexedDbSupported = typeof window !== 'undefined' && 'indexedDB' in window && window.indexedDB !== null;
} catch (e) {
  isIndexedDbSupported = false;
  console.warn('[Dexie] Access to window.indexedDB was blocked or is unsupported. Falling back to memory storage.');
}

const memoryCoversMap = new Map<string, LocalCover>();

if (isIndexedDbSupported) {
  try {
    class UibnCoversDatabase extends Dexie {
      covers!: Table<LocalCover>;

      constructor() {
        super('UibnCoversDatabase');
        this.version(1).stores({
          covers: 'id, syncStatus, updatedAt'
        });
      }
    }
    dexieDb = new UibnCoversDatabase();
  } catch (err) {
    console.warn('[Dexie] Failed to initialize Dexie database, falling back to memory storage:', err);
    isIndexedDbSupported = false;
  }
}

// Create a unified fail-safe interface for covers
export const coversTable = {
  get: async (id: string): Promise<LocalCover | undefined> => {
    if (isIndexedDbSupported && dexieDb) {
      try {
        return await dexieDb.covers.get(id);
      } catch (err) {
        console.warn('[Dexie] Failed to get cover from Dexie, using memory:', err);
      }
    }
    return memoryCoversMap.get(id);
  },
  put: async (item: LocalCover): Promise<string> => {
    if (isIndexedDbSupported && dexieDb) {
      try {
        await dexieDb.covers.put(item);
        return item.id;
      } catch (err) {
        console.warn('[Dexie] Failed to put cover to Dexie, using memory:', err);
      }
    }
    memoryCoversMap.set(item.id, item);
    return item.id;
  },
  update: async (id: string, changes: Partial<LocalCover> | any): Promise<number> => {
    if (isIndexedDbSupported && dexieDb) {
      try {
        return await dexieDb.covers.update(id, changes);
      } catch (err) {
        console.warn('[Dexie] Failed to update cover in Dexie, using memory:', err);
      }
    }
    const existing = memoryCoversMap.get(id);
    if (existing) {
      memoryCoversMap.set(id, { ...existing, ...changes });
      return 1;
    }
    return 0;
  },
  where: (key: string) => {
    return {
      anyOf: (values: any[]) => ({
        toArray: async (): Promise<LocalCover[]> => {
          if (isIndexedDbSupported && dexieDb) {
            try {
              return await dexieDb.covers.where(key).anyOf(values).toArray();
            } catch (err) {
              console.warn('[Dexie] Failed to query Dexie, using memory:', err);
            }
          }
          return Array.from(memoryCoversMap.values()).filter(item => 
            values.includes(item[key as keyof LocalCover])
          );
        }
      })
    };
  }
};

/**
 * Helper to convert Blob to base64 DataURL
 */
export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Saves a cover image file to local Dexie database and returns a local Object URL or DataURL
 */
export async function saveLocalCover(
  id: string,
  fileOrDataUrl: File | Blob | string,
  fileName = 'cover.webp',
  previousCoverUrl?: string
): Promise<string> {
  let blob: Blob;
  let mimeType = 'image/webp';

  if (typeof fileOrDataUrl === 'string') {
    blob = dataURLToBlob(fileOrDataUrl);
    mimeType = blob.type;
  } else {
    blob = fileOrDataUrl;
    mimeType = fileOrDataUrl.type;
  }

  await coversTable.put({
    id,
    blob,
    mimeType,
    fileName,
    syncStatus: 'pending',
    updatedAt: Date.now(),
  });

  // Try syncing right away if online
  if (navigator.onLine) {
    try {
      const remoteUrl = await syncCoverWithCloud(id, previousCoverUrl);
      if (remoteUrl) {
        return remoteUrl;
      }
    } catch (err) {
      console.warn('[Dexie] Immediate sync failed, will retry later:', err);
    }
  }

  // Return persistent Base64 Data URL fallback instead of ephemeral Object URL
  try {
    return await blobToDataURL(blob);
  } catch {
    return URL.createObjectURL(blob);
  }
}

/**
 * Deletes a remote cover image file directly from Cloudflare R2 bucket via server API
 */
export async function deleteRemoteCover(coverUrl: string): Promise<boolean> {
  if (!coverUrl || coverUrl.startsWith('data:') || coverUrl.startsWith('blob:')) {
    return false;
  }

  try {
    const token = getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/api/books/delete-cover', {
      method: 'POST',
      headers,
      body: JSON.stringify({ coverUrl }),
    });

    if (response.ok) {
      const result = await response.json();
      console.log('[R2 Delete] Successfully deleted remote cover:', coverUrl, result);
      return true;
    }
  } catch (err) {
    console.warn('[R2 Delete] Failed to call delete-cover endpoint:', err);
  }
  return false;
}

/**
 * Syncs a specific local cover to the cloud
 */
export async function syncCoverWithCloud(id: string, previousCoverUrl?: string): Promise<string | null> {
  const local = await coversTable.get(id);
  if (!local) return null;

  if (local.syncStatus === 'synced' && local.remoteUrl) {
    return local.remoteUrl;
  }

  try {
    const formData = new FormData();
    formData.append('file', local.blob, local.fileName);
    formData.append('id', id);
    if (previousCoverUrl) {
      formData.append('previousCoverUrl', previousCoverUrl);
    }

    const token = getAuthToken(); // retrieve auth token using shared utility
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/api/books/upload-cover', {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }

    const result = await response.json();
    const remoteUrl = result.url || result.coverUrl;

    if (remoteUrl) {
      await coversTable.update(id, {
        syncStatus: 'synced',
        remoteUrl,
        updatedAt: Date.now(),
      });
      return remoteUrl;
    }
    
    throw new Error('No remote URL returned from server');
  } catch (error) {
    console.error(`[Dexie Sync] Failed to sync cover ${id} to cloud:`, error);
    await coversTable.update(id, {
      syncStatus: 'failed',
      updatedAt: Date.now(),
    });
    return null;
  }
}

/**
 * Syncs all pending or failed covers with Cloud
 */
export async function syncAllPendingCovers(): Promise<number> {
  if (!navigator.onLine) return 0;

  const pending = await coversTable
    .where('syncStatus')
    .anyOf(['pending', 'failed'])
    .toArray();

  if (pending.length === 0) return 0;

  console.log(`[Dexie Sync] Found ${pending.length} pending covers to sync.`);
  let successCount = 0;

  for (const cover of pending) {
    const remoteUrl = await syncCoverWithCloud(cover.id);
    if (remoteUrl) {
      successCount++;
    }
  }

  return successCount;
}

// Automatically bind to online event to trigger syncing
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('[Dexie Sync] Network reconnected, syncing pending covers...');
    syncAllPendingCovers().catch(console.error);
  });
}

